import { useState, useEffect, useRef } from 'react';

/**
 * useState that mirrors its value to sessionStorage under the given key.
 * Lets us keep board filters/search/sort sticky as the user navigates into
 * a project detail and back, without leaking across browser sessions.
 */
export function usePersistedState(key, initial) {
  const [value, setValue] = useState(() => {
    try {
      const raw = sessionStorage.getItem(key);
      if (raw !== null) return JSON.parse(raw);
    } catch { /* ignore */ }
    return typeof initial === 'function' ? initial() : initial;
  });

  // Skip the very first write so we don't clobber stored values on remount
  // when consumers also have effects that fire on initial render.
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    try { sessionStorage.setItem(key, JSON.stringify(value)); } catch { /* ignore */ }
  }, [key, value]);

  return [value, setValue];
}

export default usePersistedState;
